// src/hooks/useConfig.js
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useMemo } from "react";
import {
  selectClientConfig,
  selectClientInfo,
  selectActiveMap,
  selectActiveMapId,
  selectAvailableMaps,
  selectAvailableStates,
  selectActiveState,
  selectActiveTierCutoffs,
  selectActiveDisplayConfig,
  selectActivePopupConfig,
  selectActiveDetailsConfig,
  selectActiveGeographyType,
  selectActiveGeographyLabels,
  selectActiveAvatar,
  selectActiveDataPaths,
  selectFeatures,
  selectStructuredReports,
  selectGlobalReports,
  selectAvatars,
  selectConfigLoading,
  selectConfigError,
  setActiveMap,
} from "../store/configSlice";

/**
 * Hook to access client configuration
 */
export const useClientConfig = () => {
  const clientConfig = useSelector(selectClientConfig);
  const clientInfo = useSelector(selectClientInfo);
  const loading = useSelector(selectConfigLoading);
  const error = useSelector(selectConfigError);

  return {
    config: clientConfig,
    clientInfo,
    loading,
    error,
  };
};

/**
 * Hook to access and switch between maps
 */
export const useMapSelector = () => {
  const dispatch = useDispatch();
  const activeMapId = useSelector(selectActiveMapId);
  const activeMap = useSelector(selectActiveMap);
  const availableMaps = useSelector(selectAvailableMaps);
  const availableStates = useSelector(selectAvailableStates);
  const activeState = useSelector(selectActiveState);

  const switchMap = useCallback(
    (mapId) => {
      dispatch(setActiveMap(mapId));
    },
    [dispatch],
  );

  // Group maps by state for the selector UI
  const mapsByState = useMemo(() => {
    const grouped = {};
    availableMaps.forEach((map) => {
      const stateAbbrev = map.stateAbbreviation;
      if (!grouped[stateAbbrev]) {
        const stateConfig = availableStates.find(
          (s) => s.abbreviation === stateAbbrev,
        );
        grouped[stateAbbrev] = {
          state: stateConfig,
          maps: [],
        };
      }
      grouped[stateAbbrev].maps.push(map);
    });
    return grouped;
  }, [availableMaps, availableStates]);

  // Group maps by geography type for the active state
  const mapsByGeoType = useMemo(() => {
    if (!activeState) return { ZCTA: [], BG: [] };

    const mapsForState = availableMaps.filter(
      (m) => m.stateAbbreviation === activeState.abbreviation,
    );

    return {
      ZCTA: mapsForState.filter((m) => m.geographyType === "ZCTA"),
      BG: mapsForState.filter((m) => m.geographyType === "BG"),
    };
  }, [availableMaps, activeState]);

  return {
    activeMapId,
    activeMap,
    availableMaps,
    availableStates,
    activeState,
    mapsByState,
    mapsByGeoType,
    switchMap,
  };
};

/**
 * Hook to access tier configuration for the active map
 */
export const useTierConfig = () => {
  const tierCutoffs = useSelector(selectActiveTierCutoffs);

  const getTierColor = useCallback(
    (tier) => {
      if (!tier || !tierCutoffs) return "#9e9e9e";

      const normalizedTier = tier.toLowerCase().trim();

      // Try direct label match
      for (const [key, config] of Object.entries(tierCutoffs)) {
        if (config.label?.toLowerCase() === normalizedTier) {
          return config.color;
        }
      }

      // Try key match with normalization
      for (const [key, config] of Object.entries(tierCutoffs)) {
        const normalizedKey = key
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .trim();
        if (
          normalizedKey === normalizedTier ||
          key.toLowerCase() === normalizedTier
        ) {
          return config.color;
        }
      }

      return "#9e9e9e";
    },
    [tierCutoffs],
  );

  const getTierLabel = useCallback(
    (tier) => {
      if (!tier || !tierCutoffs) return "No data";

      const normalizedTier = tier.toLowerCase().trim();

      for (const [key, config] of Object.entries(tierCutoffs)) {
        if (config.label?.toLowerCase() === normalizedTier) {
          return config.label;
        }
        const normalizedKey = key
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .trim();
        if (
          normalizedKey === normalizedTier ||
          key.toLowerCase() === normalizedTier
        ) {
          return config.label;
        }
      }

      return tier;
    },
    [tierCutoffs],
  );

  const tierOrder = useMemo(() => {
    if (!tierCutoffs) return [];

    return Object.entries(tierCutoffs)
      .filter(([key]) => key !== "noData")
      .sort((a, b) => (b[1].min || 0) - (a[1].min || 0))
      .map(([key, config]) => ({
        key,
        ...config,
        normalizedKey:
          config.label?.toLowerCase() ||
          key
            .replace(/([A-Z])/g, " $1")
            .toLowerCase()
            .trim(),
      }));
  }, [tierCutoffs]);

  return {
    tierCutoffs,
    getTierColor,
    getTierLabel,
    tierOrder,
  };
};

/**
 * Hook to access display configuration for the active map
 */
export const useDisplayConfig = () => {
  const displayConfig = useSelector(selectActiveDisplayConfig);
  const popupConfig = useSelector(selectActivePopupConfig);
  const detailsConfig = useSelector(selectActiveDetailsConfig);
  const geographyLabels = useSelector(selectActiveGeographyLabels);
  const geographyType = useSelector(selectActiveGeographyType);
  const activeAvatar = useSelector(selectActiveAvatar);
  const dataPaths = useSelector(selectActiveDataPaths);

  const maxScore = displayConfig?.maxScore || 100;

  return {
    displayConfig,
    popupConfig,
    detailsConfig,
    geographyLabels,
    geographyType,
    activeAvatar,
    dataPaths,
    maxScore,
    // Backward-compat: expose avatar as scoringAlgorithm shape
    scoringAlgorithm: activeAvatar
      ? {
          id: activeAvatar.id,
          name: activeAvatar.name,
          description: activeAvatar.description,
        }
      : null,
  };
};

/**
 * Hook to access feature flags
 */
export const useFeatures = () => {
  const features = useSelector(selectFeatures);

  return {
    features,
    competitorsEnabled: features?.competitors?.enabled || false,
    opportunitiesEnabled: features?.opportunities?.enabled || false,
    competitorsLabel: features?.competitors?.label || "Competitors",
    opportunitiesLabel: features?.opportunities?.label || "Opportunities",
    proxiesEnabled: features?.proxyBusinesses?.enabled || false,
    searchEnabled: features?.search?.enabled !== false,
    downloadEnabled: features?.download?.enabled || false,
    reportsEnabled: features?.reports?.enabled || false,
    searchableFields: features?.search?.searchableFields || ["city", "geoId"],
    downloadFormats: features?.download?.formats || ["xlsx", "csv"],
  };
};

/**
 * Hook to access avatars
 */
export const useAvatars = () => {
  const avatars = useSelector(selectAvatars);
  const activeAvatar = useSelector(selectActiveAvatar);

  return {
    avatars,
    activeAvatar,
  };
};

/**
 * Hook to access structured reports (v2 schema).
 *
 * Returns reports organized by the v2 hierarchy:
 * - avatars[].avatarReports (methodology)
 * - avatars[].maps[].mapReports (rankedPdf, rankedXlsx, executiveSummary)
 * - globalReports (FAQ, etc.)
 *
 * Also returns hasReports for conditional rendering.
 */
export const useReports = () => {
  const structured = useSelector(selectStructuredReports);

  const hasReports = useMemo(() => {
    if (structured.globalReports.length > 0) return true;
    return structured.avatars.some(
      ({ avatarReports, maps }) =>
        Object.keys(avatarReports).length > 0 ||
        maps.some(({ mapReports }) => Object.keys(mapReports).length > 0),
    );
  }, [structured]);

  return {
    structured,
    hasReports,
    // Convenience accessors
    avatarGroups: structured.avatars,
    globalReports: structured.globalReports,
  };
};

/**
 * Hook to format values based on field configuration
 */
export const useValueFormatter = () => {
  const formatValue = useCallback((value, format) => {
    if (value === null || value === undefined) return "N/A";

    switch (format) {
      case "number":
        return typeof value === "number" ? value.toLocaleString() : value;

      case "currency":
        return typeof value === "number" ? `$${value.toLocaleString()}` : value;

      case "percent":
        return typeof value === "number" ? `${value.toFixed(1)}%` : value;

      case "decimal":
        return typeof value === "number" ? value.toFixed(2) : value;

      default:
        return value;
    }
  }, []);

  return { formatValue };
};
